
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Type,
  Palette
} from 'lucide-react';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  showFontSizeControls?: boolean;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({ 
  value, 
  onChange, 
  placeholder = "開始輸入...",
  rows = 10,
  showFontSizeControls = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isComposing, setIsComposing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastContentRef = useRef(value);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  // 保存游標位置
  const saveSelection = useCallback(() => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const start = preCaretRange.toString().length;
    
    selectionRef.current = {
      start,
      end: start + range.toString().length
    };
  }, []);

  // 恢復游標位置
  const restoreSelection = useCallback(() => {
    if (!editorRef.current || !selectionRef.current) return;
    
    const { start, end } = selectionRef.current;
    const selection = window.getSelection();
    if (!selection) return;
    
    try {
      const createRange = (node: Node, offset: number): { node: Node; offset: number } => {
        if (node.nodeType === Node.TEXT_NODE) {
          return {
            node,
            offset: Math.min(offset, node.textContent?.length || 0)
          };
        }
        
        let currentOffset = 0;
        for (let i = 0; i < node.childNodes.length; i++) {
          const child = node.childNodes[i];
          const childLength = child.textContent?.length || 0;
          
          if (currentOffset + childLength >= offset) {
            return createRange(child, offset - currentOffset);
          }
          currentOffset += childLength;
        }
        
        return { node, offset: 0 };
      };
      
      const range = document.createRange();
      const startPos = createRange(editorRef.current, start);
      const endPos = createRange(editorRef.current, end);
      
      range.setStart(startPos.node, startPos.offset);
      range.setEnd(endPos.node, endPos.offset);
      
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (error) {
      console.warn('Could not restore selection:', error);
    }
  }, []);

  // 初始化內容
  useEffect(() => {
    if (editorRef.current && !isInitialized) {
      console.log('初始化編輯器內容:', value);
      editorRef.current.innerHTML = value || '';
      lastContentRef.current = value;
      setIsInitialized(true);
    }
  }, [value, isInitialized]);

  // 監聽 value 變化（用於表單重置等情況）
  useEffect(() => {
    if (editorRef.current && isInitialized && value !== lastContentRef.current) {
      const hadFocus = document.activeElement === editorRef.current;
      if (hadFocus) saveSelection();
      
      console.log('更新編輯器內容:', value);
      editorRef.current.innerHTML = value || '';
      lastContentRef.current = value;
      
      if (hadFocus) {
        requestAnimationFrame(() => {
          editorRef.current?.focus();
          restoreSelection();
        });
      }
    }
  }, [value, isInitialized, saveSelection, restoreSelection]);

  const handleInput = useCallback(() => {
    if (!editorRef.current || isComposing) return;
    
    const content = editorRef.current.innerHTML;
    if (content !== lastContentRef.current) {
      lastContentRef.current = content;
      onChange(content);
    }
  }, [onChange, isComposing]);

  const execCommand = useCallback((command: string, value?: string) => {
    // 確保編輯器有焦點
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    // 保存當前選擇
    saveSelection();
    
    try {
      document.execCommand(command, false, value);
    } catch (error) {
      console.warn('Command execution failed:', error);
    }
    
    // 觸發更新
    setTimeout(() => {
      handleInput();
    }, 0);
  }, [saveSelection, handleInput]);

  const handleFontSize = useCallback((size: string) => {
    execCommand('fontSize', size);
  }, [execCommand]);

  const handleTextColor = useCallback((color: string) => {
    execCommand('foreColor', color);
  }, [execCommand]);

  const isCommandActive = useCallback((command: string): boolean => {
    try {
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  }, []);

  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);

  const handleCompositionEnd = useCallback(() => {
    setIsComposing(false);
    handleInput();
  }, [handleInput]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    
    const text = e.clipboardData.getData('text/plain');
    const htmlText = text.replace(/\n/g, '<br>');
    
    execCommand('insertHTML', htmlText);
  }, [execCommand]);

  return (
    <div className="border border-gray-300 rounded-md">
      {/* 工具列 */}
      <div className="border-b border-gray-300 p-2 flex flex-wrap gap-2 bg-gray-50">
        {/* 字體格式 */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={isCommandActive('bold') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('bold')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={isCommandActive('italic') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('italic')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={isCommandActive('underline') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('underline')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Underline className="h-4 w-4" />
          </Button>
        </div>

        {/* 字體大小 */}
        {showFontSizeControls && (
          <div className="flex gap-1 items-center">
            <Type className="h-4 w-4 text-gray-600" />
            <Select onValueChange={handleFontSize}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue placeholder="大小" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">10px</SelectItem>
                <SelectItem value="2">13px</SelectItem>
                <SelectItem value="3">16px</SelectItem>
                <SelectItem value="4">18px</SelectItem>
                <SelectItem value="5">24px</SelectItem>
                <SelectItem value="6">32px</SelectItem>
                <SelectItem value="7">48px</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 文字顏色 */}
        {showFontSizeControls && (
          <div className="flex gap-1 items-center">
            <Palette className="h-4 w-4 text-gray-600" />
            <Select onValueChange={handleTextColor}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue placeholder="顏色" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="#000000">黑色</SelectItem>
                <SelectItem value="#DC2626">紅色</SelectItem>
                <SelectItem value="#059669">綠色</SelectItem>
                <SelectItem value="#2563EB">藍色</SelectItem>
                <SelectItem value="#7C3AED">紫色</SelectItem>
                <SelectItem value="#EA580C">橙色</SelectItem>
                <SelectItem value="#6B7280">灰色</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* 分隔線 */}
        <div className="border-l border-gray-300 mx-1" />

        {/* 列表 */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={isCommandActive('insertUnorderedList') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('insertUnorderedList')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={isCommandActive('insertOrderedList') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('insertOrderedList')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </div>

        {/* 分隔線 */}
        <div className="border-l border-gray-300 mx-1" />

        {/* 對齊 */}
        <div className="flex gap-1">
          <Button
            type="button"
            variant={isCommandActive('justifyLeft') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('justifyLeft')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={isCommandActive('justifyCenter') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('justifyCenter')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant={isCommandActive('justifyRight') ? 'default' : 'outline'}
            size="sm"
            onClick={() => execCommand('justifyRight')}
            onMouseDown={(e) => e.preventDefault()}
          >
            <AlignRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* 編輯區域 */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 min-h-[200px] focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:italic"
        style={{ minHeight: `${rows * 1.5}rem` }}
        onInput={handleInput}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onPaste={handlePaste}
        onBlur={saveSelection}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />
    </div>
  );
};

export default WysiwygEditor;
